import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import { GripVertical } from 'lucide-react';
import { mdToTiptap, isTiptapJson } from '../../utils/migrateMd';
import { getEditorExtensions } from './editorExtensions';
import { ALL_SLASH_ITEMS } from './EditorConfig';

import EditorToolbar from './EditorToolbar';
import FloatingToolbar from './FloatingToolbar';
import SlashMenu from './SlashMenu';
import BlockActionMenu from './BlockActionMenu';
import '../TiptapEditor.css'; // Keep the original CSS location for now

export default function TiptapEditor({
    content,
    onUpdate,
    editable = true,
    placeholder = 'Start writing, or type / for commands...',
    className = '',
    compact = false,
}) {
    const [slashOpen, setSlashOpen] = useState(false);
    const [slashFilter, setSlashFilter] = useState('');
    const [slashPos, setSlashPos] = useState(null);
    const [slashSelected, setSlashSelected] = useState(0);
    const [floatingPos, setFloatingPos] = useState(null);
    const [blockMenuOpen, setBlockMenuOpen] = useState(false);
    const [blockMenuPos, setBlockMenuPos] = useState(null);
    const [activeBlock, setActiveBlock] = useState(null);

    const slashRef = useRef(null);
    const wrapRef = useRef(null);
    const blockMenuRef = useRef(null);

    const slashOpenRef = useRef(false);
    const slashSelectedRef = useRef(0);
    const filteredSlashRef = useRef([]);
    const executeSlashRef = useRef(null);
    const closeSlashRef = useRef(null);

    const filteredSlash = ALL_SLASH_ITEMS.filter(item =>
        item.label.toLowerCase().includes(slashFilter) ||
        item.desc.toLowerCase().includes(slashFilter)
    );
    filteredSlashRef.current = filteredSlash;

    const initialContent = (() => {
        if (!content) return { type: 'doc', content: [{ type: 'paragraph' }] };
        if (typeof content === 'object' && content.type === 'doc') return content;
        if (typeof content === 'string') {
            if (isTiptapJson(content)) {
                try { return JSON.parse(content); } catch { /* fall through */ }
            }
            return mdToTiptap(content);
        }
        return { type: 'doc', content: [{ type: 'paragraph' }] };
    })();

    const editor = useEditor({
        extensions: getEditorExtensions(placeholder),
        content: initialContent,
        editable,
        onUpdate: ({ editor }) => {
            onUpdate?.(editor.getJSON());
        },
        onSelectionUpdate: ({ editor }) => {
            if (!editable) return;
            const { from, to } = editor.state.selection;
            if (from === to) {
                setFloatingPos(null);
                return;
            }
            const coords = editor.view.coordsAtPos(from);
            const wrapEl = wrapRef.current;
            if (wrapEl) {
                const wrapRect = wrapEl.getBoundingClientRect();
                setFloatingPos({
                    top: coords.top - wrapRect.top - 44,
                    left: coords.left - wrapRect.left,
                });
            }
        },
        editorProps: {
            handleKeyDown: (view, event) => {
                if (slashOpenRef.current) {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setSlashSelected(i => {
                            const next = Math.min(i + 1, filteredSlashRef.current.length - 1);
                            slashSelectedRef.current = next;
                            return next;
                        });
                        return true;
                    }
                    if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        setSlashSelected(i => {
                            const next = Math.max(i - 1, 0);
                            slashSelectedRef.current = next;
                            return next;
                        });
                        return true;
                    }
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        executeSlashRef.current?.(slashSelectedRef.current);
                        return true;
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        closeSlashRef.current?.();
                        return true;
                    }
                }
                return false;
            },
        },
    });

    const promptImage = useCallback(() => {
        if (!editor) return;
        const url = window.prompt('Enter image URL:');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    }, [editor]);

    const closeSlash = useCallback(() => {
        setSlashOpen(false);
        setSlashFilter('');
        setSlashPos(null);
        slashOpenRef.current = false;
    }, []);

    slashOpenRef.current = slashOpen;
    slashSelectedRef.current = slashSelected;
    closeSlashRef.current = closeSlash;

    const executeSlash = useCallback((index) => {
        if (!editor) return;
        const item = filteredSlashRef.current[index];
        if (!item) return;

        const { state } = editor;
        const { $from } = state.selection;
        const textBefore = $from.parent.textContent.slice(0, $from.parentOffset);
        const slashMatch = textBefore.match(/\/([a-zA-Z]*)$/);
        if (slashMatch) {
            const deleteFrom = $from.pos - slashMatch[0].length;
            editor.chain().focus().deleteRange({ from: deleteFrom, to: $from.pos }).run();
        }

        item.command(editor, { promptImage });
        closeSlash();
    }, [editor, promptImage, closeSlash]);

    executeSlashRef.current = executeSlash;

    useEffect(() => {
        if (!editor || !editable) return;
        const handleClick = (e) => {
            const { from, to } = editor.state.selection;
            if (from === to) setFloatingPos(null);
            if (blockMenuRef.current && !blockMenuRef.current.contains(e.target) && !e.target.closest('.tiptap-drag-handle-inner')) {
                setBlockMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [editor, editable]);

    useEffect(() => {
        if (!editor || !editable) return;

        const handleTransaction = () => {
            const { state } = editor;
            const { $from } = state.selection;
            const textBefore = $from.parent.textContent.slice(0, $from.parentOffset);

            const slashMatch = textBefore.match(/\/([a-zA-Z]*)$/);
            if (slashMatch) {
                setSlashFilter(slashMatch[1].toLowerCase());
                setSlashOpen(true);
                slashOpenRef.current = true;
                setSlashSelected(0);
                slashSelectedRef.current = 0;

                const coords = editor.view.coordsAtPos($from.pos);
                const wrapEl = wrapRef.current;
                if (wrapEl) {
                    const wrapRect = wrapEl.getBoundingClientRect();
                    setSlashPos({
                        top: coords.bottom - wrapRect.top + 4,
                        left: coords.left - wrapRect.left,
                    });
                }
            } else {
                if (slashOpenRef.current) {
                    closeSlashRef.current?.();
                }
            }
        };

        editor.on('transaction', handleTransaction);
        return () => editor.off('transaction', handleTransaction);
    }, [editor, editable]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(editable);
        }
    }, [editor, editable]);

    if (!editor) return null;

    return (
        <div ref={wrapRef} className={`tiptap-wrap ${className} ${editable ? '' : 'tiptap-wrap--readonly'}`}>
            {editable && !compact && <EditorToolbar editor={editor} />}
            {editable && floatingPos && <FloatingToolbar editor={editor} position={floatingPos} />}

            <div className="tiptap-editor-area">
                <EditorContent editor={editor} />
                <DragHandle
                    editor={editor}
                    className={`tiptap-drag-handle ${editable ? '' : 'tiptap-drag-handle--hidden'}`}
                    pluginKey="DragHandle"
                    nested={true}
                    onNodeChange={({ node, pos }) => setActiveBlock({ node, pos })}
                >
                    {editable && (
                        <div
                            className="tiptap-drag-handle-inner"
                            onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wrapEl = wrapRef.current;
                                if (wrapEl) {
                                    const wrapRect = wrapEl.getBoundingClientRect();
                                    setBlockMenuPos({
                                        top: rect.top - wrapRect.top,
                                        left: rect.left - wrapRect.left + 24,
                                    });
                                    setBlockMenuOpen(o => !o);
                                }
                            }}
                        >
                            <GripVertical size={16} />
                        </div>
                    )}
                </DragHandle>
                {blockMenuOpen && blockMenuPos && activeBlock && activeBlock.node && (
                    <BlockActionMenu 
                        editor={editor}
                        activeBlock={activeBlock}
                        position={blockMenuPos}
                        forwardRef={blockMenuRef}
                        onClose={() => setBlockMenuOpen(false)}
                    />
                )}
            </div>

            {slashOpen && slashPos && filteredSlash.length > 0 && (
                <SlashMenu 
                    editor={editor}
                    position={slashPos}
                    filter={slashFilter}
                    selectedIndex={slashSelected}
                    ref={slashRef}
                    promptImage={promptImage}
                    onHoverUrl={(idx) => setSlashSelected(idx)}
                />
            )}
        </div>
    );
}
